"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.merchantOrderProgress = merchantOrderProgress;
const STEP_LABEL = {
    placed: 'Order placed',
    paid: 'Paid',
    agreement: 'Agreement signed',
    setup: 'Setting up your devices',
    tested: 'Tested together',
    packed: 'Packed',
    shipped: 'Shipped',
    delivered: 'Delivered',
    live: 'Live',
};
const HEADLINE = {
    placed: 'We have your order',
    paid: 'Confirming your payment',
    agreement: 'Waiting for the agreement to be signed',
    setup: "We're setting up your devices",
    tested: "We're testing everything together",
    packed: "We're packing your order",
    shipped: 'Your order is about to ship',
    delivered: 'Your order is on its way',
    live: 'Delivered — ready for your first sale',
};
const LANE_LABEL = {
    merchant_account: 'Merchant account',
    card_reader: 'Card reader',
    till: 'Till',
    accessories: 'Accessories',
};
/** What each internal stage is called when a merchant reads it. */
const MERCHANT_WORDING = {
    allocated: 'Reserving your devices',
    compliance: 'Checking your business documents',
    merchant_assigned: 'Choosing your card processor',
    var_sheet: 'Setting up your merchant account',
    provision_payment: 'Preparing your card reader',
    emv_setup_config: 'Configuring card payments',
    emv_download_params: 'Loading payment settings',
    register_emv: 'Registering with your processor',
    configure_payment: 'Final card reader checks',
    provisioned: 'Installing the till software',
    create_register: 'Creating your register',
    device_couple: 'Pairing the till and card reader',
    till_handover: 'Creating your owner login and PIN',
    payment_types: 'Setting up how you take payment',
    button_layout: 'Setting up your buttons and screens',
    packages_verified: 'Checking your software',
    provision_accessories: 'Preparing your accessories',
    accessories_ready: 'Getting accessories ready to pack',
};
/** Which merchant step each internal stage rolls into. Lane stages resolve separately. */
const STEP_OF = {
    account: 'placed', package: 'placed', placed: 'placed',
    payment: 'paid', payment_verified: 'paid', packages_activated: 'paid',
    agreement: 'agreement',
    test_transaction: 'tested', refund_transaction: 'tested', receipt_printing: 'tested', barcode_scanning: 'tested',
    packed: 'packed', shipped: 'shipped', delivered: 'delivered', live: 'live',
};
/** Spine stages that are really part of setting up the till. */
const TILL_SPINE = new Set(['device_couple', 'till_handover', 'payment_types', 'button_layout', 'packages_verified']);
const ORDER = ['placed', 'paid', 'agreement', 'setup', 'tested', 'packed', 'shipped', 'delivered', 'live'];
function laneOfStage(r) {
    if (r.def.lane === 'card')
        return 'merchant_account';
    if (r.def.lane === 'payment_devices')
        return 'card_reader';
    if (r.def.lane === 'pos_devices' || TILL_SPINE.has(r.def.id))
        return 'till';
    if (r.def.lane === 'accessories')
        return 'accessories';
    return null;
}
function merchantOrderProgress(resolved, devices = []) {
    const live = resolved.filter((r) => r.state !== 'skipped');
    const steps = new Map(ORDER.map((id) => [id, { stages: [] }]));
    const lanes = new Map();
    const allocated = live.find((r) => r.def.id === 'allocated');
    for (const r of live) {
        const lane = laneOfStage(r);
        if (lane) {
            if (!lanes.has(lane))
                lanes.set(lane, { stages: [] });
            lanes.get(lane).stages.push(r);
            steps.get('setup').stages.push(r);
            continue;
        }
        const step = STEP_OF[r.def.id];
        if (step)
            steps.get(step).stages.push(r);
    }
    // Reserving stock is the first job of every DEVICE lane that has devices.
    const deviceLane = {
        merchant_account: null, card_reader: 'payment', till: 'pos', accessories: 'accessory',
    };
    if (allocated) {
        for (const [id, b] of lanes) {
            if (id !== 'merchant_account')
                b.stages.unshift(allocated);
        }
        steps.get('setup').stages.push(allocated);
    }
    const isDone = (r) => r.state === 'done';
    // The last step with real evidence of being done; everything before it is implied.
    let lastDoneIdx = -1;
    ORDER.forEach((id, i) => {
        const b = steps.get(id);
        if (b.stages.length && b.stages.every(isDone))
            lastDoneIdx = i;
    });
    const stepDone = (id, i) => {
        const b = steps.get(id);
        if (i < lastDoneIdx)
            return true;
        return b.stages.length > 0 && b.stages.every(isDone);
    };
    const maxAt = (rs) => {
        const t = Math.max(0, ...rs.map((r) => r.at || 0));
        return t || undefined;
    };
    let currentAssigned = false;
    const outSteps = ORDER
        .filter((id) => id === 'setup' || steps.get(id).stages.length > 0 || ORDER.indexOf(id) < lastDoneIdx)
        .map((id) => {
        const i = ORDER.indexOf(id);
        const done = stepDone(id, i);
        let state = done ? 'done' : 'todo';
        if (!done && !currentAssigned) {
            state = 'current';
            currentAssigned = true;
        }
        return { id, label: STEP_LABEL[id], state, ...(done ? { at: maxAt(steps.get(id).stages) } : {}) };
    });
    const setupIdx = ORDER.indexOf('setup');
    const outLanes = ['merchant_account', 'card_reader', 'till', 'accessories']
        .filter((id) => lanes.get(id)?.stages.length)
        .map((id) => {
        const stages = lanes.get(id).stages;
        const implied = setupIdx < lastDoneIdx;
        const doneCount = implied ? stages.length : stages.filter(isDone).length;
        const pct = Math.round((doneCount / stages.length) * 100);
        const next = implied ? undefined : stages.find((r) => !isDone(r));
        const state = pct === 100 ? 'done' : doneCount > 0 ? 'current' : 'todo';
        return {
            id,
            label: LANE_LABEL[id],
            pct,
            state,
            ...(next ? { now: MERCHANT_WORDING[next.def.id] || next.def.label } : {}),
            unitIds: devices.filter((d) => deviceLane[id] && d.lane === deviceLane[id]).map((d) => d.unitId),
        };
    });
    // Overall: every stage the merchant's rail covers, with implied ones counted.
    let total = 0;
    let done = 0;
    ORDER.forEach((id, i) => {
        const b = steps.get(id);
        total += b.stages.length;
        done += i < lastDoneIdx ? b.stages.length : b.stages.filter(isDone).length;
    });
    const current = outSteps.find((s) => s.state === 'current');
    return {
        pct: total ? Math.round((done / total) * 100) : 0,
        headline: current ? HEADLINE[current.id] : 'Live — your till is trading',
        steps: outSteps,
        lanes: outLanes,
    };
}
//# sourceMappingURL=merchantOrderProgress.js.map